import { useGLTF } from '@react-three/drei';
import { Suspense, useEffect, useMemo } from 'react';
import {
  Box3,
  Color,
  Mesh,
  Object3D,
  Quaternion,
  Vector3,
  type MeshStandardMaterial,
  type Texture,
} from 'three';
import { SURAH_AYAHS, surahOfBuilding, type Building, type RegionTree } from '@map/shared';
import type { Territory } from '@map/worldkit';
import { Neglect } from './Neglect.js';
import { glowTexture } from './planetSkin.js';

/**
 * Buildings standing on the ground they belong to.
 *
 * Every building is a model from Kenney's City Kit (CC0, kenney.nl — licence
 * alongside the models in public/models), always drawn whole, and which model
 * says how big it is against the rest of the Qur'an: the shortest surah gets
 * the smallest model, Al-Baqarah the tallest tower, and every other surah sits
 * between them in proportion to its ayahs.
 */
export interface TerrainBuildingsProps {
  territories: Map<string, Territory>;
  tree: RegionTree;
  buildings: Building[];
  selectedId: string | null;
}

const WEATHERED = '#6f6a60';

/**
 * The kit from smallest to grandest, grouped where models stand the same
 * height so a tier can vary without lying about size. The very wide models
 * are left out: shrunk to fit a plot they would read smaller than they are.
 */
const LADDER: readonly (readonly string[])[] = [
  ['building-c'],
  ['building-a', 'building-b', 'building-d', 'building-h'],
  ['building-f', 'building-g', 'building-i'],
  ['building-l'],
  ['building-skyscraper-a'],
  ['building-m'],
  ['building-skyscraper-c', 'building-skyscraper-e'],
  ['building-skyscraper-b'],
  ['building-skyscraper-d'],
];

/** The ladder runs from the shortest surah to the longest, in ayahs. */
const SMALLEST = Math.min(...SURAH_AYAHS);
const LARGEST = Math.max(...SURAH_AYAHS);

const url = (name: string) => `/models/${name}.glb`;

function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return h >>> 0;
}

/**
 * Straight along the ladder by the whole surah's length, so a portion of
 * Al-Baqarah is still Al-Baqarah's tower. Anything that is not a surah goes by
 * its own floors on the same scale. Within a tier the building's id picks, so
 * the same surah is always the same building.
 */
function modelFor(building: Building): string {
  const surah = surahOfBuilding(building.name);
  const size = surah ? (SURAH_AYAHS[surah - 1] ?? SMALLEST) : building.floorsTotal;
  const t = (size - SMALLEST) / (LARGEST - SMALLEST);
  const tier = LADDER[Math.round(Math.min(1, Math.max(0, t)) * (LADDER.length - 1))] ?? LADDER[0] ?? [];
  return tier[hash(building.id) % tier.length] ?? 'building-c';
}

export function TerrainBuildings({ territories, tree, buildings, selectedId }: TerrainBuildingsProps) {
  // One soft billboard, shared by every plume on the planet.
  const puff = useMemo(() => glowTexture(), []);
  useEffect(() => () => puff?.dispose(), [puff]);

  const sites = useMemo(() => {
    const out: Array<{ building: Building; territory: Territory; width: number }> = [];
    for (const building of buildings) {
      // Prayers are the river (SalahRiver), surahs are camels (Camels) and
      // Ilm's people are figures (People); what is left here is built floor
      // by floor.
      if (building.kind !== 'floors' || surahOfBuilding(building.name) !== null) continue;
      const territory = territories.get(building.regionId);
      if (!territory || !tree.regions[building.regionId]) continue;
      // Sized to its own ground, so a building never overruns its plot.
      const width = Math.min(0.06, Math.max(0.012, Math.sqrt(territory.area) * 0.075));
      out.push({ building, territory, width });
    }
    return out;
  }, [buildings, territories, tree]);

  if (sites.length === 0) return null;

  return (
    <group>
      {sites.map(({ building, territory, width }) => (
        <Suspense key={building.id} fallback={null}>
          <ModelSite
            building={building}
            model={modelFor(building)}
            site={territory.site}
            width={width}
            puff={puff}
            highlighted={building.regionId === selectedId}
          />
        </Suspense>
      ))}
    </group>
  );
}

/** Lays a group flat on the sphere at `site`, with +Y pointing straight up. */
function useSurfaceTransform(site: { x: number; y: number; z: number }) {
  return useMemo(() => {
    const up = new Vector3(site.x, site.y, site.z).normalize();
    const quaternion = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), up);
    const helper = new Object3D();
    // Land is drawn at 1.02, so anything standing on it has to start there:
    // at a radius of 1 the buildings were underground.
    helper.position.copy(up.clone().multiplyScalar(1.0205));
    helper.quaternion.copy(quaternion);
    return { position: helper.position.clone(), quaternion };
  }, [site.x, site.y, site.z]);
}

/** A model's footprint this wide is drawn at the plot's width; the rest keep proportion to it. */
const REFERENCE_FOOTPRINT = 1.4;

function ModelSite({
  building,
  model,
  site,
  width,
  puff,
  highlighted,
}: {
  building: Building;
  model: string;
  site: { x: number; y: number; z: number };
  width: number;
  puff: Texture | null;
  highlighted: boolean;
}) {
  const { position, quaternion } = useSurfaceTransform(site);
  const { scene } = useGLTF(url(model));

  // One scale for every ordinary building, so the models keep their true
  // heights against each other and a taller building really is a bigger job.
  // A copy per site, with its own materials, so weathering one does not grey
  // the rest.
  const { object, height } = useMemo(() => {
    const copy = scene.clone(true);
    const bounds = new Box3().setFromObject(copy);
    const size = bounds.getSize(new Vector3());
    const scale = width / REFERENCE_FOOTPRINT;
    copy.scale.setScalar(scale);

    const weathered = new Color(WEATHERED);
    copy.traverse((node) => {
      if (!(node instanceof Mesh)) return;
      const list = (Array.isArray(node.material) ? node.material : [node.material]) as MeshStandardMaterial[];
      const tinted = list.map((material) => {
        const own = material.clone();
        if (building.weathering > 0) own.color.lerp(weathered, building.weathering * 0.7);
        return own;
      });
      node.material = Array.isArray(node.material) ? tinted : tinted[0];
    });
    return { object: copy, height: size.y * scale };
  }, [scene, width, building.weathering]);

  return (
    <group position={position} quaternion={quaternion}>
      <primitive object={object} />

      {/* Neglect, rising off the roof: a wisp, then smoke, then flames. */}
      <Neglect weathering={building.weathering} width={width} roof={height} texture={puff} />

      {highlighted && (
        <mesh position={[0, 0.0005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[width * 0.8, width * 1.0, 32]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.5} />
        </mesh>
      )}
    </group>
  );
}

for (const tier of LADDER) for (const name of tier) useGLTF.preload(url(name));
